"use client";

import { useState, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Rocket,
  Building2,
  Store,
  Users,
  ShoppingBag,
  Warehouse,
  Globe,
  Handshake,
  TrendingUp,
  Package,
  ArrowRight,
  Check,
  ChevronDown,
} from "lucide-react";
import { Container } from "@/components/ui/container";
import { Section } from "@/components/ui/section";
import { generalFaq } from "@/content/data/faq";
import { siteConfig } from "@/lib/constants";

const audienceCards = [
  {
    icon: Rocket,
    title: "Just Starting",
    description: "Launch your store with zero inventory risk",
    benefits: ["No upfront costs", "Proven products", "Learn from partners"],
    cta: "Start Free",
    href: siteConfig.links.shopifyApp,
    color: "from-emerald-500 to-teal-600",
  },
  {
    icon: Store,
    title: "Marketplace Seller",
    description: "Expand your product catalog instantly",
    benefits: ["1000s of products", "Auto sync", "No warehouse needed"],
    cta: "Expand Catalog",
    href: siteConfig.links.shopifyApp,
    color: "from-blue-500 to-indigo-600",
  },
  {
    icon: Handshake,
    title: "Partner Dropshipper",
    description: "Source from trusted Shopify brands",
    benefits: ["Verified suppliers", "Auto fulfillment", "Brand quality"],
    cta: "Find Partners",
    href: siteConfig.links.shopifyApp,
    color: "from-violet-500 to-purple-600",
  },
  {
    icon: Users,
    title: "Ambassador",
    description: "Earn commissions on every referral",
    benefits: ["Passive income", "No inventory", "Track earnings"],
    cta: "Start Earning",
    href: siteConfig.links.shopifyApp,
    color: "from-pink-500 to-rose-600",
  },
  {
    icon: ShoppingBag,
    title: "Online Retailer",
    description: "Scale your store without limits",
    benefits: ["Unlimited products", "AI sales boost", "Global reach"],
    cta: "Scale Now",
    href: siteConfig.links.shopifyApp,
    color: "from-orange-500 to-amber-600",
  },
  {
    icon: Building2,
    title: "Offline Retailer",
    description: "Bridge your online & offline business",
    benefits: ["Omnichannel", "POS sync", "Inventory unified"],
    cta: "Go Online",
    href: siteConfig.links.shopifyApp,
    color: "from-cyan-500 to-sky-600",
  },
  {
    icon: Package,
    title: "Supplier",
    description: "Reach thousands of retail partners",
    benefits: ["New channels", "Auto orders", "B2B & D2C"],
    cta: "List Products",
    href: siteConfig.links.shopifyApp,
    color: "from-green-500 to-emerald-600",
  },
  {
    icon: Warehouse,
    title: "Wholesaler",
    description: "B2B commerce at scale",
    benefits: ["Bulk pricing", "Net terms", "Order management"],
    cta: "Go Wholesale",
    href: siteConfig.links.shopifyApp,
    color: "from-slate-500 to-zinc-600",
  },
  {
    icon: Globe,
    title: "Global Brand",
    description: "Expand internationally with local partners",
    benefits: ["Local suppliers", "Fast shipping", "Multi-currency"],
    cta: "Go Global",
    href: siteConfig.links.shopifyApp,
    color: "from-indigo-500 to-violet-600",
  },
  {
    icon: TrendingUp,
    title: "Scaling Fast",
    description: "Grow your revenue streams",
    benefits: ["Multiple channels", "AI conversion", "Analytics"],
    cta: "Accelerate",
    href: siteConfig.links.shopifyApp,
    color: "from-red-500 to-orange-600",
  },
];

function AudienceCard({
  icon: Icon,
  title,
  description,
  benefits,
  cta,
  href,
  color,
}: {
  icon: typeof Rocket;
  title: string;
  description: string;
  benefits: string[];
  cta: string;
  href: string;
  color: string;
}) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="group shrink-0 w-[340px] block"
    >
      <div className="relative h-full p-5 rounded-2xl bg-white border border-stone-200/60 shadow-sm transition-all duration-300 hover:shadow-xl hover:border-stone-300/80 hover:-translate-y-1.5 ring-1 ring-transparent hover:ring-stone-100">
        <div className="flex items-start gap-3 mb-3">
          <div
            className={`w-10 h-10 rounded-xl bg-gradient-to-br ${color} flex items-center justify-center shrink-0 transition-transform duration-300 group-hover:scale-110`}
          >
            <Icon className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="font-semibold text-stone-900 leading-tight">
              {title}
            </h3>
            <p className="text-xs text-stone-500 mt-0.5">{description}</p>
          </div>
        </div>

        <ul className="space-y-1.5 mb-4">
          {benefits.map((benefit) => (
            <li
              key={benefit}
              className="flex items-center gap-2 text-xs text-stone-600"
            >
              <Check className="w-3.5 h-3.5 text-primary-500 shrink-0" />
              {benefit}
            </li>
          ))}
        </ul>

        <span className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-600 group-hover:text-primary-700 transition-colors">
          {cta}
          <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" />
        </span>
      </div>
    </a>
  );
}

function GeneralFAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: 0.2 }}
      className="mt-16 max-w-3xl mx-auto"
    >
      <h3 className="text-h3 text-stone-900 text-center mb-8">
        Frequently Asked Questions
      </h3>
      <div className="space-y-3">
        {generalFaq.map((faq, index) => (
          <div
            key={index}
            className="bg-white rounded-xl border border-stone-200 overflow-hidden"
          >
            <button
              onClick={() => setOpenIndex(openIndex === index ? null : index)}
              className="w-full flex items-center justify-between p-4 text-left"
            >
              <span className="font-medium text-stone-900">{faq.question}</span>
              <ChevronDown
                className={`w-5 h-5 text-stone-400 transition-transform ${
                  openIndex === index ? "rotate-180" : ""
                }`}
              />
            </button>
            <AnimatePresence>
              {openIndex === index && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <div className="px-4 pb-4">
                    <p className="text-stone-600 text-sm">{faq.answer}</p>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        ))}
      </div>
    </motion.div>
  );
}

function InfiniteCarousel() {
  const [isPaused, setIsPaused] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const startX = useRef(0);
  const scrollLeft = useRef(0);
  const hasMoved = useRef(false);

  // Duplicate cards for seamless loop
  const duplicatedCards = [...audienceCards, ...audienceCards];

  // Mouse handlers
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (!containerRef.current) return;
    setIsDragging(true);
    setIsPaused(true);
    hasMoved.current = false;
    startX.current = e.pageX - containerRef.current.offsetLeft;
    scrollLeft.current = containerRef.current.scrollLeft;
    e.preventDefault();
  }, []);

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (!isDragging || !containerRef.current) return;
      const x = e.pageX - containerRef.current.offsetLeft;
      const walk = (x - startX.current) * 1.5;
      if (Math.abs(walk) > 5) hasMoved.current = true;
      containerRef.current.scrollLeft = scrollLeft.current - walk;
    },
    [isDragging],
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    setTimeout(() => setIsPaused(false), 150);
  }, []);

  const handleMouseLeave = useCallback(() => {
    if (isDragging) {
      setIsDragging(false);
      setTimeout(() => setIsPaused(false), 150);
    }
  }, [isDragging]);

  // Touch handlers
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (!containerRef.current) return;
    setIsDragging(true);
    setIsPaused(true);
    hasMoved.current = false;
    startX.current = e.touches[0].pageX - containerRef.current.offsetLeft;
    scrollLeft.current = containerRef.current.scrollLeft;
  }, []);

  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      if (!isDragging || !containerRef.current) return;
      const x = e.touches[0].pageX - containerRef.current.offsetLeft;
      const walk = (x - startX.current) * 1.5;
      if (Math.abs(walk) > 5) hasMoved.current = true;
      containerRef.current.scrollLeft = scrollLeft.current - walk;
    },
    [isDragging],
  );

  const handleTouchEnd = useCallback(() => {
    setIsDragging(false);
    setTimeout(() => setIsPaused(false), 150);
  }, []);

  // Prevent clicks after drag
  const handleClick = useCallback((e: React.MouseEvent) => {
    if (hasMoved.current) {
      e.preventDefault();
      e.stopPropagation();
    }
  }, []);

  return (
    <div className="relative">
      {/* Gradient fade on edges */}
      <div className="absolute left-0 top-0 h-full w-24 bg-gradient-to-r from-white to-transparent z-10 pointer-events-none" />
      <div className="absolute right-0 top-0 h-full w-24 bg-gradient-to-l from-white to-transparent z-10 pointer-events-none" />

      <div
        ref={containerRef}
        className={`draggable-carousel flex gap-6 px-4 py-10 ${isDragging ? "is-dragging" : ""}`}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onClickCapture={handleClick}
      >
        <div
          className="flex gap-6 animate-scroll-left-slow"
          style={{
            willChange: "transform",
            animationPlayState: isPaused ? "paused" : "running",
          }}
        >
          {duplicatedCards.map((card, index) => (
            <AudienceCard
              key={`${card.title}-${index}`}
              icon={card.icon}
              title={card.title}
              description={card.description}
              benefits={card.benefits}
              cta={card.cta}
              href={card.href}
              color={card.color}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

export function AudienceSection() {
  return (
    <Section background="white">
      <Container>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <h2 className="text-display-1 text-stone-900 mb-4">
            Built for Every Stage of Growth
          </h2>
          <p className="text-body-lg max-w-3xl mx-auto">
            Whether you&apos;re just starting or scaling to millions, Buddify
            adapts to your needs.
          </p>
        </motion.div>
      </Container>

      {/* Full-width carousel */}
      <div className="w-full">
        <InfiniteCarousel />
      </div>

      {/* General FAQs */}
      <Container>
        <GeneralFAQ />
      </Container>
    </Section>
  );
}
